import {
  ComponentMultiStyleConfig,
  Text,
  Textarea,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import ResizeTextarea from "react-textarea-autosize";
import React, { forwardRef, useMemo } from "react";
import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";

export type CMFormInputBaseTextAreaProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "error" | "diff";
  size?: "sm" | "md" | "lg";

  /**
   * TextAreaのサイズ変更可否。
   * noneの場合はサイズ変更不可。
   */
  resize?: "vertical" | "horizontal" | "none";

  /**
   * trueなら、入力文字数に応じて自動リサイズを行う。
   */
  autoResize?: boolean;

  /**
   * TextAreaの最小表示行数
   * autoResizeがtrueの場合のみ用いる。
   * 指定した行数分のTextAreaを初期表示する。
   * 3を設定した場合、表示する文字列が1行分しかない場合でも3行分のエリアが表示される。
   */
  minRows?: number;

  /**
   * TextAreaの最大表示行数
   * autoResizeがtrueの場合のみ用いる。
   * 指定した行数分までのTextAreaを初期表示する。
   * 3を設定した場合、表示する文字列が5行分ある場合でも3行分のエリアのみ表示される。
   */
  maxRows?: number;

  placeholder?: string;

  /**
   * 値。
   */
  value?: string | null;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: string | null;

  onChange?: (changedValue: string) => void;
  onBlur?: () => void;
} & ComponentStyleProps;

/**
 * TextAreaコンポーネント.
 *
 * TextAreaの変更可否や、モードの切り替えが可能。
 * 入力文字数に応じて自動でサイズを変更する。
 * minRows = maxRows となる場合は自動でサイズを変更しない。（表示サイズ固定）
 * 入力文字が表示エリアを超える場合はスクロールバーが表示される。
 */
export const CMFormInputBaseTextArea = forwardRef<
  HTMLTextAreaElement,
  CMFormInputBaseTextAreaProps
>(function TextAreaBase(
  {
    editMode = "editable",
    variant = "default",
    size = "md",
    resize = "vertical",
    autoResize = false,
    minRows = 1,
    maxRows,

    placeholder,
    value = null,

    changedFrom = null,

    onChange,
    onBlur,

    sx,
    ...rest
  }: CMFormInputBaseTextAreaProps,
  ref,
) {
  const styles = useMultiStyleConfig("CMFormInputBaseTextAreaStyles", {
    size,
  });
  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const disabled = editMode === "disabled" || editMode === "readOnly";
  const isInvalid = variant === "error";

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    onChange?.(value);
  };

  /**
   * 差分モード時、値が変更前と異なるならtrue。
   * 以下のいずれかの場合はfalse。
   * - 差分モードではない。
   * - 変更前と値が同一である。
   */
  const hasChangedInDiffMode = useMemo(
    () => isDiffMode && value !== changedFrom,
    [isDiffMode, value, changedFrom],
  );

  const textareaStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );
  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  return editMode === "readOnly" ? (
    <Text sx={readonlyStyles} {...rest}>
      {value}
    </Text>
  ) : autoResize ? (
    <Textarea
      disabled={disabled}
      isInvalid={isInvalid}
      size={size}
      resize={resize}
      placeholder={placeholder}
      value={value ?? ""}
      ref={ref}
      minH="unset"
      minRows={minRows}
      maxRows={maxRows}
      as={ResizeTextarea}
      onChange={handleChange}
      onBlur={onBlur}
      sx={{ ...textareaStyles, ...sx }}
      {...rest}
    />
  ) : (
    <Textarea
      disabled={disabled}
      isInvalid={isInvalid}
      size={size}
      resize={resize}
      placeholder={placeholder}
      value={value ?? ""}
      ref={ref}
      minH="unset"
      onChange={handleChange}
      onBlur={onBlur}
      sx={{ ...textareaStyles, ...sx }}
      {...rest}
    />
  );
});

export const CMFormInputBaseTextAreaStyles: ComponentMultiStyleConfig = {
  parts: ["containerBase", "readonly", "diff"],
  baseStyle: {
    containerBase: {
      bgColor: "white",
      _placeholder: {
        color: "blackAlpha.500",
      },
    },
    readonly: {
      maxW: "max-content",
      whiteSpace: "pre-wrap",
    },
    diff: {
      borderWidth: "1px",
      borderColor: "orange.500",
      _hover: {
        borderColor: "orange.500",
      },
      _focus: {
        _hover: {
          borderColor: "blue.500",
        },
      },
    },
  },
  sizes: {
    sm: {
      containerBase: {
        h: "120px",
        px: "6px",
      },
    },
    md: {
      containerBase: {
        h: "120px",
        px: "8px",
      },
    },
    lg: {
      containerBase: {
        h: "120px",
        px: "10px",
      },
    },
  },
};
